import { Component, Prop, Vue } from 'vue-property-decorator';
import { SeamlessCoreStore } from '@seamless/store';
import VueSeamlessStore from '@seamless/vue-store-plugin';
import {
  addConnectionAemEnvironmentConnection,
  connectionName as aemEnvConnectionName,
  ConnectionAemEnvironmentState,
} from '@owc/connection-aem-environment';

import { initializeComponentRegistry } from '@owc/component-registry-connection';

import {
  addUserConnection,
  CIASClaims,
  connectionName,
  getUserConnection,
  UserConnection,
  AuthState,
} from '@b2x/login-library';

import { Logger, APP_COMPONENTS } from '@b2x/utils';
import { Stages } from '@b2x/login-library';
import { b2xlcComponentConfig } from './interfaces/types';

const logger = new Logger('b2xLC', APP_COMPONENTS.B2X_LC);

Vue.use(new VueSeamlessStore());

@Component
export default class App extends Vue {
  userConnection!: UserConnection;
  componentRegistry!: any;
  store!: SeamlessCoreStore;
  claims: CIASClaims | null = null;

  /**
   * Following three properties are only made for legacy header integration,
   * in Header v2 integration, they are retrieved through @owc/component-registry-connection
   */
  @Prop({ type: String, default: 'Hi' })
  dataLoggedinSalutation!: string;

  @Prop({ type: String, default: 'Logout' })
  dataLoggedinLabel!: string;

  @Prop({ type: String, default: 'Login' })
  dataLoggedoutLabel!: string;

  // https://pages.git.daimler.com/dh-io-mbmxp/seamless/#/guidelines/store/component-registry?id=component-registry
  @Prop({ type: String })
  componentId!: string;

  /** we want to keep default to be legacy so that we don't have to change initial
   * header integration which is not meant to be maintained
   */
  @Prop({ type: String, default: 'LEGACY' })
  integrationContext!: string;

  @Prop({ type: String })
  country!: string;

  @Prop({ type: String })
  language!: string;

  stage!: Stages;

  _country = '';

  _language = '';

  translations: Record<string, string> = {
    login: this.dataLoggedoutLabel,
    logout: this.dataLoggedinLabel,
  };

  async mounted(): Promise<void> {
    this._country = this.country;
    this._language = this.language;

    logger.log('login component mounted, salutation', this.dataLoggedinSalutation);

    await addUserConnection();
    this.userConnection = await getUserConnection();

    await addConnectionAemEnvironmentConnection();

    this.store.subscribe(connectionName, this.onAuthEvent);

    this.store.subscribe(aemEnvConnectionName, this.onAEMEvent);

    if (window.top.aemNamespace) {
      this.componentRegistry = initializeComponentRegistry();
      this.componentRegistry.subscribe(this.componentId, this.onComponentConfig);
    }

    // this is where we initialize when in demo env or legacy header
    // for header v2 integration check onAEMEvent() method
    if ((this._country && this._language) || this.integrationContext === 'LEGACY') {
      // in case init params have been provided via attributes, we pass them along,
      // if we are in legacy header, init params will be detected in environmentHelper
      this.userConnection.initAuthentication(this.createAuthInitParams());
    }
  }

  onLogin(): void {
    logger.log('onLogin called');

    // check for claims is needed as there is only one button, but it shows text for both login and logout actions,
    // We only need to call login action as logout happens in the user menu, not from this button
    if (!this.claims) {
      this.userConnection.authenticate(this.createAuthInitParams());
    }
  }

  onLogout(): void {
    logger.log('onLogout called');

    this.userConnection.logout();
  }

  onAuthEvent(state: AuthState): void {
    logger.log('new store event is in', connectionName, state);

    if (state) {
      this.claims = state.auth.claims;
    }
  }

  onAEMEvent(state: ConnectionAemEnvironmentState): void {
    logger.log('aem env state', state);

    if (state.stage && state.country && state.language) {
      logger.log('calling initAuthentication after receiving AEM env data', state.stage, state.country, state.language);

      this._country = state.country;
      this._language = state.language;
      this.stage = state.stage.toLowerCase() as Stages;

      this.userConnection.initAuthentication(this.createAuthInitParams());
    }
  }

  onComponentConfig(componentConfig: b2xlcComponentConfig): void {
    logger.log('component config', { componentConfig });

    if (componentConfig.payload) {
      this.translations.login = componentConfig.payload.login;
      this.translations.logout = componentConfig.payload.logout;
    }
  }

  private createAuthInitParams() {
    return {
      country: this._country,
      language: this._language,
      stage: this.stage,
    };
  }

  beforeDestroy(): void {
    this.store.unsubscribe(connectionName, this.onAuthEvent);
    this.store.unsubscribe(aemEnvConnectionName, this.onAEMEvent);
    this.store.unsubscribe(this.componentId, this.onComponentConfig);
  }
}
